/**
 * useDecks — fetch + cache available decks (tournament + the viewer's
 * own custom decks). Refreshes on createDeck / deleteDeck via a manual
 * `refresh` call.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiPath } from '../../api.js';
import type { DeckData } from '../types.js';

type Status = 'idle' | 'loading' | 'ready' | 'error';

export type DecksHook = {
  status: Status;
  decks: readonly DeckData[];
  error: string | null;
  refresh: () => Promise<void>;
  create: (
    name: string,
    cardIds: readonly string[],
    visibility?: 'public' | 'private',
  ) => Promise<string | null>;
  clone: (sourceDeckId: string) => Promise<string | null>;
  remove: (deckId: string) => Promise<boolean>;
};

async function fetchDecks(): Promise<readonly DeckData[]> {
  const res = await fetch(apiPath('/api/decks?game_id=wonders'), { credentials: 'include' });
  if (!res.ok) throw new Error(`/api/decks returned ${res.status}`);
  const json = (await res.json()) as { ok: boolean; decks: readonly DeckData[] };
  if (!json.ok) throw new Error('decks response malformed');
  return json.decks;
}

export function useDecks(): DecksHook {
  const [status, setStatus] = useState<Status>('idle');
  const [decks, setDecks] = useState<readonly DeckData[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const fetched = await fetchDecks();
      setDecks(fetched);
      setStatus('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown error');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (
      name: string,
      cardIds: readonly string[],
      visibility: 'public' | 'private' = 'private',
    ): Promise<string | null> => {
      const res = await fetch(apiPath('/api/decks'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ game_id: 'wonders', name, card_ids: cardIds, visibility }),
      });
      if (!res.ok) return null;
      const json = (await res.json()) as { ok: boolean; deck_id?: string };
      if (!json.ok || !json.deck_id) return null;
      await refresh();
      return json.deck_id;
    },
    [refresh],
  );

  const clone = useCallback(
    async (sourceDeckId: string): Promise<string | null> => {
      const res = await fetch(apiPath(`/api/decks/${sourceDeckId}/clone`), {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const json = (await res.json()) as { ok: boolean; deck_id?: string };
      if (!json.ok || !json.deck_id) return null;
      await refresh();
      return json.deck_id;
    },
    [refresh],
  );

  const remove = useCallback(
    async (deckId: string): Promise<boolean> => {
      const res = await fetch(apiPath(`/api/decks/${deckId}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) return false;
      await refresh();
      return true;
    },
    [refresh],
  );

  return { status, decks, error, refresh, create, clone, remove };
}
